import { useState } from 'react'
import { Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type { ProductComponentLink } from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { V2ImportBomButton } from './V2ImportBomButton'
import { V2LinkProductComponentDialog } from './V2LinkProductComponentDialog'
import { countDistinctPartSuppliers } from '../utils/productParts'
import { getV2ProductPartsPath } from '../utils/routes'

type V2ProductPartsSummaryProps = {
  productId: string
  productComponents: ProductComponentLink[]
  isLoading: boolean
  onImportFailures?: (
    failures: Array<{ row: number; sku?: string; reason: string }>
  ) => void
}

export function V2ProductPartsSummary({
  productId,
  productComponents,
  isLoading,
  onImportFailures,
}: V2ProductPartsSummaryProps) {
  const { t } = useTranslation('common')
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const partsPath = getV2ProductPartsPath(productId)
  const partsCount = productComponents.length
  const suppliersCount = countDistinctPartSuppliers(productComponents)

  if (isLoading) {
    return (
      <Card className='h-fit' aria-busy='true'>
        <CardHeader className='space-y-1 pb-3'>
          <div className='flex items-center justify-between gap-2'>
            <Skeleton className='h-5 w-16' />
            <Skeleton className='h-8 w-40' />
          </div>
          <Skeleton className='h-4 w-full max-w-xs' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-4 w-40' />
          <Skeleton className='h-8 w-24' />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className='h-fit'>
        <CardHeader className='space-y-1 pb-3'>
          <div className='flex items-center justify-between gap-2'>
            <CardTitle className='text-base leading-tight'>
              {t('v2.pages.products.detail.parts.heading')}
            </CardTitle>
            <div className='flex shrink-0 flex-wrap items-center justify-end gap-2'>
              <V2ImportBomButton
                productId={productId}
                onImported={(result) => {
                  if (result.failed.length > 0) {
                    onImportFailures?.(result.failed)
                  }
                }}
              />
              <Button
                className='gap-2'
                onClick={() => {
                  setLinkDialogOpen(true)
                }}
                size='sm'
                type='button'
                variant='outline'
              >
                <Link2 className='size-4' aria-hidden />
                {t('v2.pages.products.detail.parts.linkButton')}
              </Button>
            </div>
          </div>
          <CardDescription className='text-xs'>
            {t('v2.pages.products.detail.parts.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <dl className='space-y-1 text-sm'>
            <div className='flex flex-wrap gap-x-2'>
              <dt className='text-muted-foreground'>
                {t('v2.pages.products.detail.parts.summary.partsLabel')}
              </dt>
              <dd className='font-medium'>
                {t('v2.pages.products.detail.parts.summary.partsCount', {
                  count: partsCount,
                })}
              </dd>
            </div>
            <div className='flex flex-wrap gap-x-2'>
              <dt className='text-muted-foreground'>
                {t('v2.pages.products.detail.parts.summary.suppliersLabel')}
              </dt>
              <dd className='font-medium'>
                {t('v2.pages.products.detail.parts.summary.suppliersCount', {
                  count: suppliersCount,
                })}
              </dd>
            </div>
          </dl>
          <Button asChild size='sm' variant='secondary'>
            <Link to={partsPath}>
              {t('v2.pages.products.detail.parts.viewAllButton')}
            </Link>
          </Button>
        </CardContent>
      </Card>

      <V2LinkProductComponentDialog
        productId={productId}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
    </>
  )
}
